import * as React from "react";
import { Button, Combobox, Input } from "@/components/ui";
import { SegmentationDetailServices, PlayersServices, GetContentPagesFilters } from "@/services/bedrock";
import { usePaginatedCombobox, useSocialCoachSession } from "@/utils";
import { useSuperPageSearch } from "@/utils/useSuperPageSearch";
import { useEffect, useRef, useState } from "react";

export interface VideoPagesSearchProps {
  onSearchChange: (searchFilters: GetContentPagesFilters) => void;
}

export const VideoPagesSearch: React.FC<VideoPagesSearchProps> = ({ onSearchChange }) => {
  const { isPlayer } = useSocialCoachSession();
  const [search, setSearch] = useState<string>();
  const [searchInput, setSearchInput] = useState("");
  const [myPages, setMyPages] = useState<boolean>();
  const [playerId, setPlayerId] = useState<number>();
  const [superPageId, setSuperPageId] = useState<number>();
  const [segmentationId, setSegmentationId] = useState<number[]>();

  const lastSearchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedSearch = searchInput.trim();
      setSearch(trimmedSearch ? trimmedSearch : undefined);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const searchFilters: GetContentPagesFilters = {
      search,
      playerId,
      superPageId,
      segmentationId,
      myPages,
    };
    const key = JSON.stringify(searchFilters);
    if (lastSearchKeyRef.current === key) return;

    lastSearchKeyRef.current = key;
    onSearchChange(searchFilters);
  }, [onSearchChange, search, myPages, playerId, superPageId, segmentationId]);

  return (
    <div className="flex gap-4 items-center flex-wrap flex-auto">
      <Input
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        placeholder="Search"
        className="max-w-80 min-w-50"
      />
      {isPlayer ? (
        <PlayerFilters onMyPagesChange={setMyPages} />
      ) : (
        <CoachFilters
          onPlayerIdChange={setPlayerId}
          onSuperPageIdChange={setSuperPageId}
          onSegmentationIdChange={setSegmentationId}
        />
      )}
    </div>
  );
};

interface PlayerFiltersProps {
  onMyPagesChange: (myPages: boolean | undefined) => void;
}

const PlayerFilters: React.FC<PlayerFiltersProps> = ({ onMyPagesChange }) => {
  const [myPages, setMyPages] = useState<boolean>();

  useEffect(() => {
    onMyPagesChange(myPages);
  }, [myPages]);

  const selectedButtonClass = "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground";

  return (
    <div className="border-border border-1 rounded-lg">
      <Button
        variant="ghost"
        children="All Pages"
        className={myPages === undefined ? selectedButtonClass : ""}
        onClick={() => setMyPages(undefined)}
      />
      <Button
        variant="ghost"
        children="My Pages"
        className={myPages === true ? selectedButtonClass : ""}
        onClick={() => setMyPages(true)}
      />
      <Button
        variant="ghost"
        children="Marketing Pages"
        className={myPages === false ? selectedButtonClass : ""}
        onClick={() => setMyPages(false)}
      />
    </div>
  );
};

interface CoachFiltersProps {
  onPlayerIdChange: (playerId: number | undefined) => void;
  onSuperPageIdChange: (superPageId: number | undefined) => void;
  onSegmentationIdChange: (segmentationId: number[] | undefined) => void;
}

const CoachFilters: React.FC<CoachFiltersProps> = ({
  onPlayerIdChange,
  onSuperPageIdChange,
  onSegmentationIdChange,
}) => {
  const { fetchSuperPages } = useSuperPageSearch();

  const getSuperPages = async (search: string | undefined, page: number) => {
    const result = await fetchSuperPages({ search, page, perPage: 25 });
    const parsedResult = {
      ...result,
      content: result.content.map(superPage => ({
        id: superPage.id,
        label: superPage.title,
      })),
    };
    if (page === 1) {
      parsedResult.content.unshift({
        id: -1,
        label: "Standalone (no Super Page)",
      });
    }
    return parsedResult;
  };
  const getSegmentationDetails = (search: string | undefined, page: number) => {
    const types = ["STATE", "REGION", "BRANCH", "COST_CENTER"];
    return SegmentationDetailServices.getSegmentationDetails(types, search, page, 25);
  };
  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    const parsedResult = {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: player.fullName || player.email || "-",
      })),
    };
    if (page === 1) {
      parsedResult.content.unshift({
        id: -1,
        label: "Your Video Pages",
      });
    }
    return parsedResult;
  };

  const superPages = usePaginatedCombobox(getSuperPages, false);
  const segmentationDetails = usePaginatedCombobox(getSegmentationDetails, true);
  const players = usePaginatedCombobox(getPlayers, false);
  useEffect(() => {
    onPlayerIdChange(players.value?.id);
  }, [players.value]);
  useEffect(() => {
    onSuperPageIdChange(superPages.value?.id);
  }, [superPages.value]);
  useEffect(() => {
    onSegmentationIdChange(segmentationDetails.value.length > 0 ? segmentationDetails.value.map(s => s.id) : undefined);
  }, [segmentationDetails.value]);

  return (
    <>
      <Combobox
        {...superPages}
        renderMenuItem={({ item, label, icon, badge }) => {
          if (item.id === -1) {
            return (
              <>
                {icon}
                {badge}
                <div className="italic">{label}</div>
              </>
            );
          } else {
            return undefined;
          }
        }}
        showClear
        placeholder="Filter by Source"
        className="max-w-80 min-w-50"
      />
      <Combobox
        {...segmentationDetails}
        itemToStringLabel={segmentationDetail => segmentationDetail.name || "-"}
        itemToBadge={segmentationDetail => segmentationDetail.type}
        placeholder="Filter by Segmentation"
        className="max-w-80 min-w-50"
      />
      <Combobox
        {...players}
        renderMenuItem={({ item, label, icon, badge }) => {
          if (item.id === -1) {
            return (
              <>
                {icon}
                {badge}
                <div className="italic">{label}</div>
              </>
            );
          } else {
            return undefined;
          }
        }}
        showClear
        placeholder="Filter by Owner"
        className="max-w-80 min-w-50"
      />
    </>
  );
};
